import {
  Button,
  Center,
  Container,
  Flex,
  IconButton,
  Select,
  Text,
  VStack,
  chakra,
} from '@chakra-ui/react';
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import { MdClose } from 'react-icons/md';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import CollectionFilters from '@repo/ui-components/tailwind/CollectionGrid/CollectionFilters';
import FilterTags from './FilterTags';
import hiddenMavenoid from '../../../utils/hiddenMavenoid';
import { acceptedLifecycle } from './sorters';

export default function MobileFiltersAndSortMenu({
  activeFilters,
  handleFilterTag,
  clearFilters,
  location,
  handleFilters,
  handleSort,
  sort,
  chunks,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { tt } = useUiComponentsInternationalContext();

  const SORTERS = useMemo(
    () => ({
      featured: {
        title: tt(s => s.blocksDeptComponents.featuredCollectionSorterLabel),
        sortFunc: () => {},
      },
      newest: {
        title: tt(s => s.blocksDeptComponents.newestCollectionSorterLabel),
        reverse: true,
        sortFunc: product => product?.shopifyProduct?.publishedAt,
      },
      priceAsc: {
        title: tt(
          s => s.blocksDeptComponents.priceLowToHighCollectionSorterLabel
        ),
        sortFunc: product => {
          const prices = product.shopifyProduct.variants
            .filter(v => acceptedLifecycle.includes(v.variantLifecycle))
            .map(v => v.price);
          return Math.min(...prices);
        },
      },
      priceDesc: {
        title: tt(
          s => s.blocksDeptComponents.priceHighToLowCollectionSorterLabel
        ),
        reverse: true,
        sortFunc: product => {
          const prices = product.shopifyProduct.variants
            .filter(v => acceptedLifecycle.includes(v.variantLifecycle))
            .map(v => v.price);
          return Math.min(...prices);
        },
      },
    }),
    [tt]
  );

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'auto';

    hiddenMavenoid(menuOpen);
  }, [menuOpen]);

  const selectedFilters = activeFilters
    .flatMap(({ filters }) => filters)
    .filter(f => f.active);

  return (
    <>
      <Button
        variant="tertiary"
        onClick={() => setMenuOpen(!menuOpen)}
        rightIcon={
          <Center>
            {selectedFilters.length ? (
              <Center w="4" h="4" borderRadius="full" bg="primary.base" p="2">
                <Text
                  color="typography.lighter"
                  textStyle="caption"
                  pos="relative"
                  fontSize="10px"
                >
                  {selectedFilters.length}
                </Text>
              </Center>
            ) : null}
            <HiAdjustmentsHorizontal size="24" />
          </Center>
        }
      >
        {tt(s => s.blocksDeptComponents.filterSortButton)}
      </Button>
      <Container
        pos="fixed"
        w="100vw"
        h="100vh"
        bg="white"
        justify="flex-start"
        top={menuOpen ? `0px` : '-100vh'}
        transition="top .3s ease-in-out"
        left="0"
        zIndex="9999"
        display={{ base: 'flex', lg: 'none' }}
        overflowY="scroll"
      >
        <VStack w="full" pb="24" h="max">
          <Flex w="full" justify="flex-end" py="4">
            <IconButton
              icon={<MdClose size={24} />}
              variant="tertiary"
              w="10"
              h="10"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={tt(s => s.blocksDeptComponents.closeButton)}
            />
          </Flex>
          <Flex
            justify="space-between"
            align="center"
            py="2"
            pos="relative"
            w="full"
          >
            <Text
              textStyle="body"
              as="h4"
              pos="relative"
              fontWeight="light"
              sx={{ textWrap: 'nowrap' }}
            >
              {chunks.flatMap(c => [...c]).length}{' '}
              {tt(s => s.blocksDeptComponents.resultsLabel)}
            </Text>
            <Select
              w="max"
              maxW={{ base: '50%', md: 'unset' }}
              onChange={handleSort}
              value={sort}
              bg="white"
              textStyle="body"
              fontWeight="light"
            >
              {Object.entries(SORTERS).map(([value, { title }]) => (
                <chakra.option
                  key={value}
                  value={value}
                  textStyle="body"
                  fontWeight="light"
                >
                  {title}
                </chakra.option>
              ))}
            </Select>
          </Flex>
          {activeFilters.length ? (
            <FilterTags
              handleFilterTag={handleFilterTag}
              clearFilters={clearFilters}
              activeFilters={activeFilters}
              location={location}
            />
          ) : null}

          {activeFilters.length ? (
            <CollectionFilters
              activeFilters={activeFilters}
              handleFilters={handleFilters}
            />
          ) : null}
        </VStack>
      </Container>
    </>
  );
}

MobileFiltersAndSortMenu.propTypes = {
  activeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      active: PropTypes.bool,
    })
  ).isRequired,
  handleFilterTag: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  handleFilters: PropTypes.func.isRequired,
  handleSort: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  chunks: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))).isRequired,
};
